class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close())
    // Use event delegation so the listener survives DOM replacement
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') this.close()
    })
    this.setHeaderCartIconAccessibility()
  }

  connectedCallback() {
    this.getCart()
  }

  getCart() {
    fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((sections) => {
        this._updateSections(sections)
        this._updateSiteWideSalePrices(sections['cart-drawer'])

        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html')
        const isEmpty = html.querySelector('.drawer__inner-empty') !== null
        this.classList.toggle('is-empty', isEmpty)
      })
      .catch((error) => {
        console.error('Error fetching latest cart:', error)
      })
  }

  _updateSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      if (sectionElement && sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector)
      }
    })
    this.initializeFreeSampleSlider()
  }

  _updateSiteWideSalePrices(cartDrawerHtml) {
    const html = new DOMParser().parseFromString(cartDrawerHtml, 'text/html')
    const cartTotalElement = html.querySelector('[data-total-price]')
    if (!cartTotalElement) return

    const finalPrice = parseInt(cartTotalElement.dataset.totalPrice, 10) || 0
    const siteWideSaleTierOneDiscount = parseInt(this.dataset.siteWideSaleTierOneDiscount, 10) || 0
    const siteWideSaleTierTwoThreshold = parseInt(this.dataset.siteWideSaleTierTwoThreshold, 10) || 0

    if (siteWideSaleTierOneDiscount <= 0) return

    document.querySelectorAll('[data-site-wide-sale-tier-one]').forEach(($siteWideSaleTierOne) => {
      const $priceWrapper = $siteWideSaleTierOne.closest('[id^="price-"]')
      if (!$priceWrapper) return
      const $siteWideSaleTierTwo = $priceWrapper.querySelector('[data-site-wide-sale-tier-two]')
      if (!$siteWideSaleTierTwo) return

      const isASiteWideDiscountTierPriceVisible =
        $siteWideSaleTierOne.style.display !== 'none' || $siteWideSaleTierTwo.style.display !== 'none'

      if (!isASiteWideDiscountTierPriceVisible) return

      if (finalPrice >= siteWideSaleTierTwoThreshold) {
        $siteWideSaleTierOne.style.display = 'none'
        $siteWideSaleTierOne.removeAttribute('selected')
        $siteWideSaleTierTwo.style.display = 'inline-block'
        $siteWideSaleTierTwo.setAttribute('selected', true)
      } else {
        $siteWideSaleTierTwo.style.display = 'none'
        $siteWideSaleTierTwo.removeAttribute('selected')
        $siteWideSaleTierOne.style.display = 'inline-block'
        $siteWideSaleTierOne.setAttribute('selected', true)
      }
    })
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy) {
    // Don't open if we are in the hybrid-cart group
    // if (window.CART_AB_TEST === "hybrid-cart" || !window.CART_AB_TEST) return;
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote)
    const overlay = this.querySelector('#CartDrawer-Overlay')
    const splideSamples = this.querySelector('.splide-free-samples')
    if (splideSamples) {
      splideSamples.style.display = 'block'
    }
    setTimeout(() => {
      this.classList.add('animate', 'active')
      if (overlay) {
        overlay.classList.remove('opacity-0')
        overlay.classList.add('opacity-100')
      }
    })

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)

        // Initialize Splide slider on first open
        this.initializeFreeSampleSlider()
      },
      { once: true }
    )

    document.body.classList.add('overflow-hidden')
  }

  close() {
    const overlay = this.querySelector('#CartDrawer-Overlay')
    const splideSamples = this.querySelector('.splide-free-samples')
    if (splideSamples) {
      splideSamples.style.display = 'none'
    }

    if (overlay) {
      overlay.classList.remove('opacity-100')
      overlay.classList.add('opacity-0')
    }

    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty')
    this.productId = parsedState.id

    this._updateSections(parsedState.sections)

    setTimeout(() => {
      this.open()
    })
  }

  initializeFreeSampleSlider() {
    const freeSampleSplide = this.querySelector('.splide.splide-free-samples')
    if (freeSampleSplide && typeof Splide !== 'undefined') {
      // Destroy existing instance if it exists
      if (freeSampleSplide.splide) {
        freeSampleSplide.splide.destroy(true)
      }

      const slider = new Splide(freeSampleSplide, {
        type: 'slide',
        perPage: 3,
        perMove: 1,
        gap: '1rem',
        pagination: false,
        arrows: false,
      }).mount()

      if (typeof SplideHorizontalScroll !== 'undefined') {
        SplideHorizontalScroll(slider)
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ]
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((parsedState) => {
        const $cartDrawer = document.querySelector('cart-drawer')

        if ($cartDrawer._updateSiteWideSalePrices) {
          $cartDrawer._updateSiteWideSalePrices(parsedState['cart-drawer'])
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id)

          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector)
        })

        if ($cartDrawer.initializeFreeSampleSlider) {
          $cartDrawer.initializeFreeSampleSlider()
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)
